import { useToast } from "@/hooks/use-toast";
import { accountsKeys } from "@/shared/api/hooks/use-accounts";
import { applicationsKeys } from "@/shared/api/hooks/use-applications";
import { transactionsKeys } from "@/shared/api/hooks/use-transactions";
import {
  createWebSocketConnectionWithReconnect,
  type WebSocketEvent,
} from "@/shared/api/websocket";
import { queryClient } from "@/shared/stores/query-client";
import { useUIStore, type ActivityEvent } from "@/shared/stores/ui-store";
import { useCallback, useEffect, useRef } from "react";

export function useRealTimeUpdates() {
  const activityLogEvents = useUIStore((state) => state.activityLogEvents);
  const addActivityLogEvent = useUIStore((state) => state.addActivityLogEvent);
  const isWebSocketEnabled = useUIStore((state) => state.isWebSocketEnabled);
  const setWebSocketConnected = useUIStore((state) => state.setWebSocketConnected);
  const { toast } = useToast();
  const wsRef = useRef<{ disconnect: () => void; reconnect: () => void } | null>(null);

  const toastRef = useRef(toast);
  const addActivityLogEventRef = useRef(addActivityLogEvent);

  useEffect(() => {
    toastRef.current = toast;
    addActivityLogEventRef.current = addActivityLogEvent;
  }, [toast, addActivityLogEvent]);

  const addEvent = useCallback((event: Omit<ActivityEvent, "id" | "timestamp">) => {
    addActivityLogEventRef.current(event);

    if (event.status === "error") {
      toastRef.current({
        title: `${event.type} Error`,
        description: event.description,
        variant: "destructive",
      });
    } else if (event.status === "success" && event.action.includes("created")) {
      toastRef.current({
        title: `New ${event.type}`,
        description: event.description,
      });
    } else if (event.status === "warning") {
      toastRef.current({
        title: `${event.type} Warning`,
        description: event.description,
        variant: "default",
      });
    }
  }, []);

  const invalidateCacheForEvent = useCallback((wsEvent: WebSocketEvent) => {
    const eventType = wsEvent.type?.toLowerCase();
    const eventId = wsEvent.id;

    if (!eventType) return;

    switch (eventType) {
      case "account":
        if (eventId) {
          queryClient.invalidateQueries({ queryKey: accountsKeys.detail(eventId) });
        }
        queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
        break;
      case "application":
        if (eventId) {
          queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(eventId) });
        }
        queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
        break;
      case "transaction":
        if (eventId) {
          queryClient.invalidateQueries({ queryKey: transactionsKeys.detail(eventId) });
        }
        queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
        break;
      case "webhook":
        queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
        queryClient.invalidateQueries({ queryKey: transactionsKeys.lists() });
        if (eventId) {
          queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(eventId) });
          queryClient.invalidateQueries({ queryKey: transactionsKeys.detail(eventId) });
        }
        break;
      case "config":
      case "location":
        queryClient.invalidateQueries();
        break;
      default:
        break;
    }
  }, []);

  const mapWebSocketEventToActivityEvent = useCallback(
    (wsEvent: WebSocketEvent): Omit<ActivityEvent, "id" | "timestamp"> | null => {
      const eventType = wsEvent.type?.toLowerCase();

      if (!eventType) {
        if (import.meta.env.DEV) {
          console.warn("[useRealTimeUpdates] Event type is missing:", wsEvent);
        }
        return null;
      }

      invalidateCacheForEvent(wsEvent);

      const allowedTypes = ["account", "application", "transaction", "webhook", "config", "location"];
      if (!allowedTypes.includes(eventType)) {
        if (import.meta.env.DEV) {
          console.warn(
            `[useRealTimeUpdates] Event type "${eventType}" is not in allowed types:`,
            wsEvent
          );
        }
        return null;
      }

      const typeCapitalized = eventType.charAt(0).toUpperCase() + eventType.slice(1);

      let action = "updated";
      let description = "";

      if (wsEvent.operation === "insert") {
        action = "created";
        description = `${typeCapitalized} created`;
      } else if (wsEvent.operation === "update") {
        action = wsEvent.status ? "status_changed" : "updated";
        if (wsEvent.status) {
          description = `${typeCapitalized} status changed to ${wsEvent.status}`;
        } else {
          description = `${typeCapitalized} updated`;
        }
      } else if (wsEvent.operation === "delete") {
        action = "deleted";
        description = `${typeCapitalized} deleted`;
      } else {
        action = wsEvent.status ? "status_changed" : "updated";
        description = wsEvent.status
          ? `${typeCapitalized} status changed to ${wsEvent.status}`
          : `${typeCapitalized} ${action}`;
      }

      description = description.replace(/ to unknown$/i, "");

      const status = wsEvent.status?.toLowerCase() || "";
      let eventStatus: ActivityEvent["status"] = "success";
      if (status.includes("error") || status.includes("failed") || status.includes("declined")) {
        eventStatus = "error";
      } else if (status.includes("warning") || status.includes("pending")) {
        eventStatus = "warning";
      } else if (
        status.includes("completed") ||
        status.includes("approved") ||
        status.includes("active")
      ) {
        eventStatus = "success";
      } else if (status === "unknown" || status === "") {
        eventStatus = "info";
      } else {
        eventStatus = "info";
      }

      return {
        type: eventType as ActivityEvent["type"],
        action,
        objectId: wsEvent.id || "",
        externalId: wsEvent.externalId || "",
        description,
        status: eventStatus,
      };
    },
    [invalidateCacheForEvent]
  );

  useEffect(() => {
    if (!isWebSocketEnabled) {
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
        setWebSocketConnected(false);
      }
      return;
    }

    const { disconnect, reconnect } = createWebSocketConnectionWithReconnect({
      onEvent: (wsEvent) => {
        const activityEvent = mapWebSocketEventToActivityEvent(wsEvent);
        if (activityEvent) {
          addEvent(activityEvent);
        }
      },
      onError: (error) => {
        if (import.meta.env.DEV) {
          console.error("[useRealTimeUpdates] WebSocket error:", error);
        }
        setWebSocketConnected(false);
      },
      onOpen: () => {
        setWebSocketConnected(true);
      },
      onClose: () => {
        setWebSocketConnected(false);
      },
    });

    wsRef.current = { disconnect, reconnect };

    return () => {
      disconnect();
      wsRef.current = null;
    };
  }, [isWebSocketEnabled, setWebSocketConnected, mapWebSocketEventToActivityEvent, addEvent]);

  return { events: activityLogEvents, addEvent };
}

let globalEventHandler: ((event: Omit<ActivityEvent, "id" | "timestamp">) => void) | null = null;

export type { ActivityEvent };

export function setGlobalEventHandler(
  handler: (event: Omit<ActivityEvent, "id" | "timestamp">) => void
) {
  globalEventHandler = handler;
}

export function emitGlobalEvent(event: Omit<ActivityEvent, "id" | "timestamp">) {
  if (globalEventHandler) {
    globalEventHandler(event);
  }
}
